
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logger } from '@/lib/logger';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { staffApiClient } from '@/api/staff';
import { useStaffConverter } from '@/hooks/staff/useStaffConverter';
import { useUserBusiness } from '@/hooks/staff/useUserBusiness';

// Import refactored components
import StaffLoading from '@/components/staff/page/StaffLoading';
import StaffError from '@/components/staff/page/StaffError';
import StaffDetailView from '@/components/staff/page/StaffDetailView';
import StaffListView from '@/components/staff/page/StaffListView';

const StaffPage = () => {
  const { staffId } = useParams<{ staffId: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { convertStaffData } = useStaffConverter();
  const { fetchCurrentUser, fetchBusinessId } = useUserBusiness();
  const [error, setError] = useState<string | null>(null);
  
  // Validate staffId - check if it's a valid UUID
  const isValidStaffId = staffId && 
    staffId !== ':staffId' && 
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(staffId);
  
  // If staffId is invalid, redirect to staff list
  useEffect(() => {
    if (staffId && !isValidStaffId) {
      logger.warn({
        message: "Invalid staff ID format in URL",
        context: { staffId }
      });
      setError("Invalid staff ID format");
      toast({
        title: "Invalid staff ID",
        description: "The staff ID in the URL is not valid. Redirecting to staff list.",
        variant: "destructive",
      });
      navigate("/app/staff");
    }
  }, [staffId, isValidStaffId, navigate, toast]);

  // Fetch current user
  const { data: userId } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 300000, // 5 minutes
  });
  
  // Fetch business ID once we have the user ID
  const { data: businessId } = useQuery({
    queryKey: ['businessId', userId],
    queryFn: () => fetchBusinessId(userId!),
    enabled: !!userId,
    staleTime: 300000, // 5 minutes
  });

  // Fetch specific staff member
  const { 
    data: aiStaff, 
    isLoading: isStaffLoading, 
    isError: isStaffError 
  } = useQuery({
    queryKey: ['staff', businessId, staffId],
    queryFn: () => {
      if (!businessId || !staffId || !isValidStaffId) return null;
      return staffApiClient.fetchStaffById(businessId, staffId).then(data => 
        data ? convertStaffData(data) : null
      );
    },
    enabled: !!businessId && !!staffId && isValidStaffId,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: any) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        setError(errorMessage);
        logger.error({
          message: "Failed to load staff data",
          error: error instanceof Error ? error : new Error(String(error)),
          context: { staffId, businessId }
        });
        toast({
          title: "Error loading staff",
          description: "There was a problem loading the staff details.",
          variant: "destructive",
        });
      }
    }
  });

  // Fetch all staff members
  const { 
    data: allAIStaff, 
    isLoading: isAllStaffLoading 
  } = useQuery({
    queryKey: ['allStaff', businessId],
    queryFn: () => {
      if (!businessId) return [];
      return staffApiClient.fetchAllStaff(businessId).then(data => 
        data.map(convertStaffData)
      );
    },
    enabled: !!businessId,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: any) => {
        logger.error({
          message: "Failed to load all staff data",
          error: error instanceof Error ? error : new Error(String(error)),
          context: { businessId }
        });
        toast({
          title: "Error loading staff list",
          description: "There was a problem loading your staff list.",
          variant: "destructive",
        });
      }
    }
  });

  // Show specific error for staff not found
  useEffect(() => {
    if (staffId && isValidStaffId && !isStaffLoading && !aiStaff && !error) {
      const errorMsg = "Staff not found or could not be loaded";
      setError(errorMsg);
      toast({
        title: "Staff not found",
        description: "Unable to find the requested staff member.",
        variant: "destructive",
      });
    }
  }, [staffId, isValidStaffId, aiStaff, isStaffLoading, error, toast]);

  // If staffId is invalid, redirect to staff list view directly
  if (staffId && !isValidStaffId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <StaffError error="Invalid staff ID format" />
      </div>
    );
  }

  // Loading states
  const isLoading = !businessId || (staffId && isStaffLoading) || (!staffId && isAllStaffLoading);
  
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <StaffLoading />
      </div>
    );
  }

  // Render error state when we have a specific staffId but couldn't load it
  if (staffId && (isStaffError || error)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <StaffError error={error || "Failed to load staff data"} />
      </div>
    );
  }

  // Render the appropriate view based on whether we have a staffId
  return (
    <div className="container mx-auto px-4 py-8">
      {staffId && isValidStaffId ? (
        <StaffDetailView staffId={staffId} aiStaff={aiStaff || null} />
      ) : (
        <StaffListView isLoading={isAllStaffLoading} allAIStaff={allAIStaff || []} />
      )}
    </div>
  );
};

export default StaffPage;
